import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search, Clock, Eye, CreditCard, Sparkles, ShieldCheck, Instagram, Globe } from "lucide-react";
import { GigmaWordmark, GigmaMark } from "../components/Logo";

export default function Landing() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur bg-white/80 border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <GigmaWordmark size={30} />
          <Link
            to="/wizard"
            data-testid="header-start-button"
            className="inline-flex items-center gap-1.5 text-sm font-medium bg-slate-900 text-white rounded-full px-4 py-2 hover:-translate-y-0.5 transition-transform"
          >
            Start Free Setup <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 gigma-dots opacity-60" />
        <div className="relative max-w-6xl mx-auto px-5 md:px-8 pt-16 md:pt-24 pb-20 md:pb-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-100 text-emerald-700 text-xs font-medium px-3 py-1.5 rounded-full mb-6">
              <Sparkles className="w-3.5 h-3.5" /> Built for businesses, schools, churches & more
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
              Get discovered on{" "}
              <span className="bg-gradient-to-r from-blue-500 via-green-500 to-amber-400 bg-clip-text text-transparent">
                Google&nbsp;Search.
              </span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
              Build your Google profile step-by-step and see how you’ll appear in
              Google Search — before publishing.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/wizard"
                data-testid="hero-start-button"
                className="inline-flex items-center gap-2 bg-slate-900 text-white rounded-full px-6 py-3.5 font-medium hover:-translate-y-0.5 transition-transform shadow-lg shadow-slate-900/10"
              >
                Start Free Setup <ArrowRight className="w-4 h-4" />
              </Link>
              <div className="flex items-center gap-3">
                <div className="flex items-baseline gap-1.5">
                  <span className="font-heading text-xl font-semibold">$9.99</span>
                  <span className="text-sm text-slate-500">one-time</span>
                </div>
                <span className="text-xs text-slate-400">· paid only after your approval</span>
              </div>
            </div>

            <div className="mt-10 flex items-center gap-5 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> No subscription</span>
              <span className="inline-flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" /> Live preview included</span>
              <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> ~5 minutes</span>
            </div>
          </div>

          {/* Hero card */}
          <div className="lg:col-span-5 relative">
            <div className="absolute -inset-6 bg-gradient-to-br from-blue-200/30 via-emerald-200/30 to-amber-100/30 blur-3xl rounded-full" />
            <div className="relative rounded-3xl border border-slate-200 bg-white p-5 gigma-card-shadow">
              <div className="flex items-center gap-2 text-xs text-slate-500 px-3 py-2 bg-slate-50 rounded-full">
                <Search className="w-3.5 h-3.5" /> riverside cuts springfield
              </div>
              <div className="mt-4 flex items-start gap-3">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 via-green-500 to-amber-400 flex items-center justify-center text-white font-heading font-semibold text-xl">RC</div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <div className="font-heading text-lg font-semibold">Riverside Cuts</div>
                    <span className="text-[10px] font-semibold uppercase tracking-wider bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">Example</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">Barber shop · 0.4 mi</div>
                  <div className="flex items-center gap-1 mt-1 text-xs">
                    <span className="text-amber-600 font-medium">4.9</span>
                    <span className="text-amber-500">★★★★★</span>
                    <span className="text-slate-400">(120)</span>
                  </div>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-1.5">
                {[0,1,2].map(i => (
                  <div key={i} className="aspect-square rounded-lg bg-gradient-to-br from-slate-100 to-slate-200" />
                ))}
              </div>
              <div className="mt-4 inline-flex items-center gap-1.5 text-xs text-emerald-600 font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 gigma-live-dot" /> Updates live as you type
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The reality — data-only facts */}
      <section className="py-20 md:py-24 bg-white border-t border-slate-100">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="max-w-2xl">
            <div className="text-xs font-medium text-amber-700 uppercase tracking-wider mb-3">The reality of local search</div>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight text-slate-900">
              Most people are invisible on Google — and don't realize it.
            </h2>
            <p className="text-slate-600 mt-4 leading-relaxed">
              The data is clear: people search Google for local businesses, schools, churches and services every day — and the ones without a strong Google profile get skipped over for the ones that do. These are the numbers, straight from Google and the industry's most cited local‑search researchers.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-4">
            {[
              { n: "46%", t: "of all Google searches have local intent — someone looking for a place, service, or business near them.", s: "HubSpot / Google" },
              { n: "76%", t: "of people who search 'near me' on their phone visit a business within 24 hours. 28% of those searches end in a purchase.", s: "Think with Google" },
              { n: "56%", t: "of local businesses still haven't claimed their Google Business Profile — an open lane for anyone who does.", s: "Local Search Association" },
              { n: "7×", t: "more clicks for complete Google profiles vs. incomplete ones. Profiles with photos get 42% more direction requests.", s: "Google" },
              { n: "87%", t: "of consumers used Google to evaluate a local business in 2022 — up from 63% just two years earlier.", s: "BrightLocal" },
              { n: "98%", t: "of consumers read online reviews before choosing a local business — no reviews often means no clicks.", s: "BrightLocal" },
            ].map((f) => (
              <div key={f.n} className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-slate-300 hover:-translate-y-0.5 transition-all">
                <div className="font-heading text-4xl font-semibold bg-gradient-to-r from-blue-500 via-green-500 to-amber-400 bg-clip-text text-transparent">
                  {f.n}
                </div>
                <p className="text-sm text-slate-700 mt-2 leading-relaxed">{f.t}</p>
                <div className="text-[11px] text-slate-400 mt-3">Source: {f.s}</div>
              </div>
            ))}
          </div>

          <div className="mt-10 max-w-3xl rounded-2xl bg-slate-900 text-white p-6 md:p-8">
            <p className="text-sm md:text-base leading-relaxed">
              Whether you run a barbershop, a landscaping company, a church, a school, a food truck or a nonprofit — if someone in your area searches for a service that you provide, one of two things is happening: they're finding you among the top searches, or they're not. Gigma helps you set yourself up for maximum exposure.
            </p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 md:py-28 bg-slate-50/60 border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="max-w-2xl">
            <div className="text-xs font-medium text-emerald-700 uppercase tracking-wider mb-3">How Gigma works</div>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight">
              From blank profile to Google-ready in three easy steps.
            </h2>
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {[
              { icon: Eye, title: "Build it visually", body: "Step by step easy guide. Your live Google preview updates as you type — so you always see what customers will see." },
              { icon: Sparkles, title: "Approve when you’re ready", body: "Review a clear summary with a completeness score. Edit anything. Nothing gets published before you give the green light." },
              { icon: CreditCard, title: "Pay $9.99 and publish", body: "Gigma walks you through 10 easy steps, you copy and paste your prefilled info along the way, and get in depth steps on how to verify (postcard, phone, email or video), go live in Search & Maps, and helps you with getting reviews." },
            ].map(({ icon: Icon, title, body }, i) => (
              <div key={title} className="rounded-3xl bg-white border border-slate-200 p-6 hover:border-slate-300 hover:-translate-y-0.5 transition-all">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 via-green-500 to-amber-400 flex items-center justify-center text-white mb-5">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="text-xs font-medium text-slate-400 mb-1">Step {i + 1}</div>
                <h3 className="font-heading text-lg font-semibold text-slate-900">{title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing strip */}
      <section className="py-20 md:py-28">
        <div className="max-w-3xl mx-auto px-5 md:px-8 text-center">
          <div className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 uppercase tracking-wider mb-4">
            <span className="w-6 h-px bg-slate-300" /> Simple pricing <span className="w-6 h-px bg-slate-300" />
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight">
            One profile. One price. Paid only after your approval.
          </h2>
          <p className="text-slate-600 mt-4 leading-relaxed">
            Building and previewing is completely free. You only pay $9.99 when you’re happy with how it looks and ready to publish.
          </p>

          {/* What's included */}
          <div className="mt-10 grid sm:grid-cols-2 gap-3 text-left max-w-2xl mx-auto">
            {[
              { t: "A personalized publish roadmap", d: "10 numbered steps with YOUR name, address, phone, website, hours and services pre-filled — copy and paste straight into Google Business Profile." },
              { t: "Verification walkthrough", d: "Plain-language explanation of postcard, phone, email and video verification — so you pick the fastest one available to you." },
              { t: "A finalized profile summary", d: "Your full structured profile, ready to reference forever. Bookmark it, share it with staff, or print it." },
              { t: "Lifetime access", d: "No subscription. Come back to your roadmap anytime — your data stays in your browser." },
            ].map((it) => (
              <div key={it.t} className="rounded-2xl border border-slate-200 bg-white p-4 flex gap-3">
                <span className="w-6 h-6 shrink-0 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-xs mt-0.5">✓</span>
                <div>
                  <div className="font-medium text-slate-900 text-sm">{it.t}</div>
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">{it.d}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 inline-flex flex-col items-center rounded-3xl border border-slate-200 bg-white p-8 gigma-card-shadow w-full max-w-sm mx-auto">
            <div className="flex items-baseline gap-1">
              <span className="font-heading text-5xl font-semibold">$9.99</span>
              <span className="text-slate-500 text-sm">USD</span>
            </div>
            <div className="text-sm text-slate-500 mt-1">One-time, no subscription</div>
            <Link
              to="/wizard"
              data-testid="pricing-start-button"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-slate-900 text-white rounded-full px-6 py-3 font-medium hover:-translate-y-0.5 transition-transform"
            >
              Start Free Setup <ArrowRight className="w-4 h-4" />
            </Link>
            <div className="text-xs text-slate-400 mt-3 flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" /> Secure checkout via Stripe
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-100 py-10">
        <div className="max-w-6xl mx-auto px-5 md:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <GigmaMark size={28} />
            <div className="flex flex-col leading-none">
              <span className="font-heading font-semibold text-slate-900 text-sm">Gigma</span>
              <span className="text-[10px] tracking-[0.18em] text-slate-400 uppercase mt-0.5">by NRFSTR</span>
            </div>
            <span className="text-sm text-slate-400 ml-4">© {new Date().getFullYear()} NRFSTR</span>
          </div>
          <div className="flex flex-col sm:items-end items-center gap-1.5">
            <div className="flex items-center gap-4 text-sm">
              <a
                href="https://instagram.com/xseandavisx"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram-link"
                className="text-slate-500 hover:text-slate-900 transition-colors inline-flex items-center gap-1.5"
              >
                <Instagram className="w-4 h-4" /> @xseandavisx
              </a>
              <a
                href="https://nrfstr.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-website-link"
                className="text-slate-500 hover:text-slate-900 transition-colors inline-flex items-center gap-1.5"
              >
                <Globe className="w-4 h-4" /> nrfstr.com
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
